import { useState, useEffect } from "react";
import { Logo } from "./Logo";

/**
 * Premium Animated Splash Screen
 * - Logo fades in with elegant animation
 * - Circular symbol gently draws itself (simulated)
 * - Smooth transition to app
 */
export const SplashScreen = ({ onComplete, minDuration = 2500 }) => {
  const [phase, setPhase] = useState("drawing"); // drawing -> revealing -> fading -> complete

  useEffect(() => {
    // Phase 1: Symbol/logo draws (0-800ms)
    const drawTimer = setTimeout(() => setPhase("revealing"), 800);
    
    // Phase 2: Text reveals fully (800-1600ms)
    const revealTimer = setTimeout(() => setPhase("ready"), 1600);
    
    // Phase 3: Fade out and complete
    const fadeTimer = setTimeout(() => setPhase("fading"), minDuration - 500);
    
    const completeTimer = setTimeout(() => {
      setPhase("complete");
      onComplete?.();
    }, minDuration);

    return () => {
      clearTimeout(drawTimer);
      clearTimeout(revealTimer);
      clearTimeout(fadeTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete, minDuration]);

  if (phase === "complete") return null;

  return (
    <div 
      className={`
        fixed inset-0 z-[100] bg-[#FAFAF7]
        flex flex-col items-center justify-center
        transition-opacity duration-500 ease-out
        ${phase === "fading" ? "opacity-0" : "opacity-100"}
      `}
    >
      {/* Centered Content */}
      <div className="flex flex-col items-center space-y-8">
        {/* Logo with Animation */}
        <div 
          className={`
            transition-all duration-700 ease-out
            ${phase === "drawing" 
              ? "opacity-0 scale-95 translate-y-4" 
              : "opacity-100 scale-100 translate-y-0"
            }
          `}
        >
          <Logo size="xl" variant="dark" />
        </div>

        {/* Tagline */}
        <p 
          className={`
            text-[#6B7280] tracking-[0.2em] text-sm uppercase
            transition-all duration-700 delay-300 ease-out
            ${phase === "drawing" || phase === "revealing"
              ? "opacity-0 translate-y-2" 
              : "opacity-100 translate-y-0"
            }
          `}
        >
          School of Consciousness
        </p>
      </div>

      {/* Subtle Loading Indicator */}
      <div 
        className={`
          absolute bottom-24 
          transition-opacity duration-500
          ${phase === "fading" ? "opacity-0" : "opacity-100"}
        `}
      >
        <div className="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-1.5 h-1.5 bg-[#4A6741] rounded-full animate-pulse"
              style={{ animationDelay: `${i * 150}ms` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SplashScreen;
